import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  TouchableOpacity,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  Alert,
  ActivityIndicator,
  Platform,
  PanResponder,
  View,
  Image,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { X, ChevronDown, ChevronLeft, ChevronRight, Upload, MapPin, Navigation } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Screen, ScreenHeader } from '@/components';
import { COLORS } from '@/constants';
import { useCreateMeetingAnnouncement } from '@/api/hooks/useMeetingAnnouncements';
import { useMeetingTypes } from '@/api/hooks/useMeetingTypes';
import { useUploadAnnouncementPhoto } from '@/api/hooks/usePhotos';
import { useNominatimSearch } from '@/api/hooks/useNominatimSearch';
import { showWarningToast, showSuccessToast, showErrorToast } from '@/utils/toast';
import type { MeetingAnnouncementInput, MeetingTypeView, OtherCityData } from '@/api/types';
import type { RootStackScreenProps } from '@/types/navigation';
import { YStack, XStack } from '@tamagui/stacks';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

type Props = RootStackScreenProps<'CreateAnnouncement'>;

// Polish month names
const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const DAYS_OF_WEEK_PL = ['ND', 'PON', 'WT', 'ŚR', 'CZW', 'PT', 'SOB'];

// Range Slider Component
type RangeSliderProps = {
  min: number;
  max: number;
  valueFrom: number;
  valueTo: number;
  onValueChange: (from: number, to: number) => void;
  trackColor?: string;
  activeTrackColor?: string;
  thumbColor?: string;
  isDark?: boolean;
};

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  valueFrom,
  valueTo,
  onValueChange,
  trackColor = '#e5e7eb',
  activeTrackColor = COLORS.PRIMARY,
  thumbColor = COLORS.PRIMARY,
  isDark = false,
}) => {
  const sliderRef = useRef<View>(null);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [activeThumb, setActiveThumb] = useState<'from' | 'to' | null>(null);

  const range = max - min;
  const fromPercent = ((valueFrom - min) / range) * 100;
  const toPercent = ((valueTo - min) / range) * 100;

  const getValueFromPosition = useCallback((x: number) => {
    if (sliderWidth === 0) return valueFrom;
    const percent = Math.max(0, Math.min(100, (x / sliderWidth) * 100));
    return Math.round(min + (percent / 100) * range);
  }, [sliderWidth, min, range, valueFrom]);

  const handleMove = useCallback((x: number) => {
    if (sliderWidth === 0 || activeThumb === null) return;
    const value = getValueFromPosition(x);

    if (activeThumb === 'from') {
      const newFrom = Math.min(value, valueTo);
      onValueChange(Math.max(min, newFrom), valueTo);
    } else {
      const newTo = Math.max(value, valueFrom);
      onValueChange(valueFrom, Math.min(max, newTo));
    }
  }, [sliderWidth, activeThumb, valueFrom, valueTo, min, max, getValueFromPosition, onValueChange]);

  // PanResponder for native
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (evt) => {
        if (sliderWidth === 0) return;
        const x = evt.nativeEvent.locationX;
        const value = getValueFromPosition(x);
        
        // Determine which thumb is closer
        const fromDistance = Math.abs(value - valueFrom);
        const toDistance = Math.abs(value - valueTo);
        
        setActiveThumb(fromDistance < toDistance ? 'from' : 'to');
      },
      onPanResponderMove: (evt) => {
        handleMove(evt.nativeEvent.locationX);
      },
      onPanResponderRelease: () => {
        setActiveThumb(null);
      },
    })
  ).current;

  // Add global mouse event listeners for web
  React.useEffect(() => {
    if (Platform.OS === 'web' && activeThumb) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (sliderRef.current) {
          const rect = (sliderRef.current as any).getBoundingClientRect?.();
          if (rect) {
            const x = e.clientX - rect.left;
            handleMove(x);
          }
        }
      };

      const handleGlobalMouseUp = () => {
        setActiveThumb(null);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [activeThumb, handleMove]);

  const containerStyle: any = { height: 40, justifyContent: 'center', paddingVertical: 10 };
  if (Platform.OS === 'web') {
    containerStyle.cursor = 'pointer';
  }

  return (
    <View
      ref={sliderRef}
      onLayout={(e) => setSliderWidth(e.nativeEvent.layout.width)}
      style={containerStyle}
      {...(Platform.OS !== 'web' ? panResponder.panHandlers : {})}
    >
      <View
        style={{
          height: 4,
          backgroundColor: trackColor,
          borderRadius: 2,
          position: 'relative',
        }}
      >
        <View
          style={{
            position: 'absolute',
            left: `${fromPercent}%`,
            right: `${100 - toPercent}%`,
            height: 4,
            backgroundColor: activeTrackColor,
            borderRadius: 2,
          }}
        />
        <TouchableOpacity
          activeOpacity={1}
          onPressIn={() => setActiveThumb('from')}
          style={(() => {
            const thumbStyle: any = {
              position: 'absolute',
              left: `${fromPercent}%`,
              width: 20,
              height: 20,
              borderRadius: 10,
              backgroundColor: thumbColor,
              marginLeft: -10,
              marginTop: -8,
              borderWidth: 2,
              borderColor: isDark ? '#1a1a1a' : COLORS.WHITE,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 3,
              elevation: 3,
              zIndex: activeThumb === 'from' ? 10 : 5,
            };
            if (Platform.OS === 'web') {
              thumbStyle.cursor = 'grab';
            }
            return thumbStyle as any;
          })()}
          {...(Platform.OS === 'web' ? {
            // @ts-ignore - web-specific props
            onMouseDown: (e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveThumb('from');
            },
          } : {})}
        />
        <TouchableOpacity
          activeOpacity={1}
          onPressIn={() => setActiveThumb('to')}
          style={(() => {
            const thumbStyle: any = {
              position: 'absolute',
              left: `${toPercent}%`,
              width: 20,
              height: 20,
              borderRadius: 10,
              backgroundColor: thumbColor,
              marginLeft: -10,
              marginTop: -8,
              borderWidth: 2,
              borderColor: isDark ? '#1a1a1a' : COLORS.WHITE,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 3,
              elevation: 3,
              zIndex: activeThumb === 'to' ? 10 : 5,
            };
            if (Platform.OS === 'web') {
              thumbStyle.cursor = 'grab';
            }
            return thumbStyle as any;
          })()}
          {...(Platform.OS === 'web' ? {
            // @ts-ignore - web-specific props
            onMouseDown: (e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveThumb('to');
            },
          } : {})}
        />
      </View>
    </View>
  );
};

// Single Slider Component
type SingleSliderProps = {
  min: number;
  max: number;
  value: number;
  onValueChange: (value: number) => void;
  trackColor?: string;
  activeTrackColor?: string;
  thumbColor?: string;
  isDark?: boolean;
};

const SingleSlider: React.FC<SingleSliderProps> = ({
  min,
  max,
  value,
  onValueChange,
  trackColor = '#e5e7eb',
  activeTrackColor = COLORS.PRIMARY,
  thumbColor = COLORS.PRIMARY,
  isDark = false,
}) => {
  const sliderRef = useRef<View>(null);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const range = max - min;
  const percent = ((value - min) / range) * 100;

  const getValueFromPosition = useCallback((x: number) => {
    if (sliderWidth === 0) return value;
    const movePercent = Math.max(0, Math.min(100, (x / sliderWidth) * 100));
    return Math.round(min + (movePercent / 100) * range);
  }, [sliderWidth, min, range, value]);

  const handleMove = useCallback((x: number) => {
    if (sliderWidth === 0 || !isDragging) return;
    const newValue = getValueFromPosition(x);
    onValueChange(Math.max(min, Math.min(max, newValue)));
  }, [sliderWidth, isDragging, min, max, getValueFromPosition, onValueChange]);

  // PanResponder for native
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        setIsDragging(true);
      },
      onPanResponderMove: (evt) => {
        handleMove(evt.nativeEvent.locationX);
      },
      onPanResponderRelease: () => {
        setIsDragging(false);
      },
    })
  ).current;

  // Add global mouse event listeners for web
  React.useEffect(() => {
    if (Platform.OS === 'web' && isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (sliderRef.current) {
          const rect = (sliderRef.current as any).getBoundingClientRect?.();
          if (rect) {
            const x = e.clientX - rect.left;
            handleMove(x);
          }
        }
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, handleMove]);

  const containerStyle: any = { height: 40, justifyContent: 'center', paddingVertical: 10 };
  if (Platform.OS === 'web') {
    containerStyle.cursor = 'pointer';
  }

  return (
    <View
      ref={sliderRef}
      onLayout={(e) => setSliderWidth(e.nativeEvent.layout.width)}
      style={containerStyle}
      {...(Platform.OS !== 'web' ? panResponder.panHandlers : {})}
    >
      <View
        style={{
          height: 4,
          backgroundColor: trackColor,
          borderRadius: 2,
          position: 'relative',
        }}
      >
        <View
          style={{
            position: 'absolute',
            left: 0,
            width: `${percent}%`,
            height: 4,
            backgroundColor: activeTrackColor,
            borderRadius: 2,
          }}
        />
        <TouchableOpacity
          activeOpacity={1}
          onPressIn={() => setIsDragging(true)}
          style={(() => {
            const thumbStyle: any = {
              position: 'absolute',
              left: `${percent}%`,
              width: 20,
              height: 20,
              borderRadius: 10,
              backgroundColor: thumbColor,
              marginLeft: -10,
              marginTop: -8,
              borderWidth: 2,
              borderColor: isDark ? '#1a1a1a' : COLORS.WHITE,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 3,
              elevation: 3,
            };
            if (Platform.OS === 'web') {
              thumbStyle.cursor = 'grab';
            }
            return thumbStyle as any;
          })()}
          {...(Platform.OS === 'web' ? {
            // @ts-ignore - web-specific props
            onMouseDown: (e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            },
          } : {})}
        />
      </View>
    </View>
  );
};

const CreateAnnouncementScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const insets = useSafeAreaInsets();
  const isDark = themeName === 'dark';
  const isMobileLayout = useIsMobileLayout();

  // Form state
  const [selectedMeetingTypeIds, setSelectedMeetingTypeIds] = useState<number[]>([]);
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [meetingAddress, setMeetingAddress] = useState('');
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<OtherCityData | null>(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [ageFrom, setAgeFrom] = useState(18);
  const [ageTo, setAgeTo] = useState(100);
  const [participantCountFrom, setParticipantCountFrom] = useState(1);
  const [participantCountTo, setParticipantCountTo] = useState(3);
  const [paymentAmount, setPaymentAmount] = useState(0);

  // Photo state
  const [selectedPhotos, setSelectedPhotos] = useState<(File | { uri: string; type: string; name: string })[]>([]);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  // Modal state
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Debounced search query for Nominatim
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(addressSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [addressSearchQuery]);

  // API hooks
  const { data: meetingTypesData = [], isLoading: isLoadingTypes } = useMeetingTypes();
  const { data: locationResults = [], isLoading: isSearchingLocations } = useNominatimSearch(debouncedSearchQuery);
  
  const uploadPhoto = useUploadAnnouncementPhoto({
    onError: (error) => {
      console.error('Error uploading photo:', error);
    },
  });

  const { mutate: createAnnouncement, isPending: isCreating } = useCreateMeetingAnnouncement({
    onSuccess: async (announcement) => {
      // Upload photos after announcement is created
      if (selectedPhotos.length > 0 && announcement?.id) {
        try {
          await Promise.all(
            selectedPhotos.map(async (photo, index) => {
              if (Platform.OS === 'web') {
                const file = photo as File;
                return uploadPhoto.mutateAsync({
                  announcementId: String(announcement.id),
                  file,
                  isDefault: index === 0, // First photo is default
                });
              } else {
                // For native, convert URI to File/Blob
                const photoData = photo as { uri: string; type: string; name: string };
                try {
                  // Fetch the image as blob
                  const response = await fetch(photoData.uri);
                  const blob = await response.blob();
                  // Create File from blob
                  const file = new File([blob], photoData.name || 'photo.jpg', { type: photoData.type || 'image/jpeg' });
                  
                  return uploadPhoto.mutateAsync({
                    announcementId: String(announcement.id),
                    file,
                    isDefault: index === 0,
                  });
                } catch (error) {
                  console.error('Error converting photo to file:', error);
                  // Return resolved promise to continue with other photos
                  return Promise.resolve();
                }
              }
            })
          );
        } catch (error) {
          console.error('Error uploading photos:', error);
          Alert.alert(
            t('common.error', 'Błąd'),
            t('createAnnouncement.photoUploadError', 'Nie udało się przesłać wszystkich zdjęć')
          );
        }
      }
      
      showSuccessToast(t('createAnnouncement.successMessage', 'Ogłoszenie zostało utworzone'));
      // Reset form
      resetForm();
      // Navigate to announcements list
      navigation.navigate('TabNavigator', { screen: 'Home' });
    },
    onError: (error) => {
      showErrorToast(
        t('createAnnouncement.errorMessage', 'Nie udało się utworzyć ogłoszenia'),
        error.message
      );
    },
  });

  const meetingTypes: MeetingTypeView[] = Array.isArray(meetingTypesData) ? meetingTypesData : [];

  // Show first 5 types, rest in "Więcej" modal
  const visibleTypes = meetingTypes.slice(0, 5);
  const moreTypes = meetingTypes.slice(5);

  const resetForm = useCallback(() => {
    setSelectedMeetingTypeIds([]);
    setSelectedDate(null);
    setSelectedTime('');
    setMeetingAddress('');
    setAddressSearchQuery('');
    setSelectedLocation(null);
    setShowLocationDropdown(false);
    setAnnouncementTitle('');
    setAdditionalInfo('');
    setAgeFrom(18);
    setAgeTo(100);
    setParticipantCountFrom(1);
    setParticipantCountTo(3);
    setPaymentAmount(0);
    setSelectedPhotos([]);
    // Reset file inputs on web
    if (Platform.OS === 'web') {
      fileInputRefs.current.forEach((ref) => {
        if (ref) ref.value = '';
      });
    }
  }, []);

  // Handle location selection from search results
  const handleLocationSelect = useCallback((location: OtherCityData) => {
    setSelectedLocation(location);
    setMeetingAddress(location.displayName);
    setAddressSearchQuery('');
    setShowLocationDropdown(false);
  }, []);

  // Reverse geocode coordinates using Nominatim
  const reverseGeocode = useCallback(async (latitude: number, longitude: number) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
      {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'TinoMobile/1.0',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Reverse geocoding failed');
    }
    
    const data = await response.json();
    const address = data.address || {};
    
    // Build short display name: "Ulica 1, Miasto"
    const city = address.city || address.town || address.village || null;
    const streetPart = address.road 
      ? (address.house_number ? `${address.road} ${address.house_number}` : address.road)
      : address.neighbourhood || address.suburb || null;
    
    const displayName = streetPart && city 
      ? `${streetPart}, ${city}`
      : city || streetPart || data.display_name || `${latitude}, ${longitude}`;
    
    return {
      displayName,
      city,
      region: address.county || null,
      state: address.state || null,
      country: address.country || null,
      lat: String(latitude),
      lon: String(longitude),
    };
  }, []);

  // Get current location - works on both web and React Native
  const handleGetCurrentLocation = useCallback(async () => {
    setIsGettingLocation(true);
    
    const onSuccess = async (latitude: number, longitude: number) => {
      try {
        const locationData = await reverseGeocode(latitude, longitude);
        setMeetingAddress(locationData.displayName);
        setSelectedLocation(locationData);
        setAddressSearchQuery('');
        setShowLocationDropdown(false);
      } catch (error) {
        console.error('Error reverse geocoding:', error);
        Alert.alert(
          t('common.error', 'Błąd'),
          t('createAnnouncement.locationError', 'Nie udało się pobrać adresu')
        );
      } finally {
        setIsGettingLocation(false);
      }
    };
    
    const onError = (errorMessage: string) => {
      setIsGettingLocation(false);
      Alert.alert(t('common.error', 'Błąd'), errorMessage);
    };

    if (Platform.OS === 'web') {
      // Web: use navigator.geolocation
      if (!navigator.geolocation) {
        onError(t('createAnnouncement.geolocationNotSupported', 'Twoja przeglądarka nie wspiera geolokalizacji'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onSuccess(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
          let message = t('createAnnouncement.locationError', 'Nie udało się pobrać lokalizacji');
          if (error.code === error.PERMISSION_DENIED) {
            message = t('createAnnouncement.locationPermissionDenied', 'Brak uprawnień do lokalizacji');
          }
          onError(message);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      // React Native: geolocation not yet configured
      // TODO: Add expo-location or @react-native-community/geolocation when building native app
      onError(t('createAnnouncement.locationNotAvailable', 'Geolokalizacja nie jest jeszcze dostępna w aplikacji mobilnej'));
    }
  }, [t, reverseGeocode]);

  // Calendar helpers
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    const days: (number | null)[] = [];

    // Add empty slots for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  }, [calendarMonth, calendarYear]);

  const handlePrevMonth = useCallback(() => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  }, [calendarMonth, calendarYear]);

  const handleNextMonth = useCallback(() => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  }, [calendarMonth, calendarYear]);

  const handleSelectDate = useCallback((day: number) => {
    const date = new Date(calendarYear, calendarMonth, day);
    setSelectedDate(date);
  }, [calendarMonth, calendarYear]);

  const isDateSelected = useCallback((day: number) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === calendarMonth &&
      selectedDate.getFullYear() === calendarYear
    );
  }, [selectedDate, calendarMonth, calendarYear]);

  const isToday = useCallback((day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === calendarMonth &&
      today.getFullYear() === calendarYear
    );
  }, [calendarMonth, calendarYear]);

  const toggleMeetingType = useCallback((typeId: number) => {
    setSelectedMeetingTypeIds(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  }, []);

  // Photo selection handlers
  const handlePhotoSelect = useCallback((index: number, file: File | { uri: string; type: string; name: string }) => {
    setSelectedPhotos((prev) => {
      const newPhotos = [...prev];
      newPhotos[index] = file;
      return newPhotos;
    });
  }, []);

  const handlePhotoRemove = useCallback((index: number) => {
    setSelectedPhotos((prev) => {
      const newPhotos = [...prev];
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  }, []);

  const handleFileInputChange = useCallback((index: number, event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        Alert.alert(
          t('common.error', 'Błąd'),
          t('createAnnouncement.invalidFileType', 'Nieprawidłowy typ pliku. Dozwolone: JPEG, PNG, GIF, WebP')
        );
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        Alert.alert(
          t('common.error', 'Błąd'),
          t('createAnnouncement.fileTooLarge', 'Plik jest za duży. Maksymalny rozmiar: 10MB')
        );
        return;
      }
      handlePhotoSelect(index, file);
    }
  }, [handlePhotoSelect, t]);

  const handlePublish = useCallback(() => {
    if (selectedMeetingTypeIds.length === 0) {
      showWarningToast(t('createAnnouncement.selectType', 'Wybierz typ spotkania'));
      return;
    }
    if (!selectedDate) {
      showWarningToast(t('createAnnouncement.selectDate', 'Wybierz datę spotkania'));
      return;
    }
    if (!selectedTime) {
      showWarningToast(t('createAnnouncement.selectTime', 'Wybierz godzinę spotkania'));
      return;
    }
    if (!meetingAddress) {
      showWarningToast(t('createAnnouncement.enterAddress', 'Wprowadź adres spotkania'));
      return;
    }

    // Use first selected type for now (API supports single type)
    const input: MeetingAnnouncementInput = {
      meetingTypeId: selectedMeetingTypeIds[0],
      meetingDate: selectedDate.toISOString().split('T')[0],
      meetingTime: selectedTime,
      meetingAddress: meetingAddress,
      additionalInfo: additionalInfo || null,
      ageFrom: ageFrom,
      ageTo: ageTo,
      participantCount: participantCountTo,
      paymentAmount: paymentAmount > 0 ? paymentAmount : null,
      coversAllCosts: paymentAmount > 0,
      status: 2,
    };

    createAnnouncement(input);
  }, [
    selectedMeetingTypeIds,
    selectedDate,
    selectedTime,
    meetingAddress,
    additionalInfo,
    ageFrom,
    ageTo,
    participantCountTo,
    paymentAmount,
    createAnnouncement,
    t,
  ]);

  // Time picker helpers
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  // Colors
  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const cardBg = isDark ? '#1a1a1a' : COLORS.WHITE;
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const subtextColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;
  const borderColor = isDark ? '#333' : '#e5e7eb';
  const chipBg = isDark ? '#2a2a2a' : '#f3f4f6';
  const chipSelectedBg = isDark ? COLORS.PRIMARY : COLORS.PRIMARY;
  const inputBg = isDark ? '#1a1a1a' : COLORS.WHITE;

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        <ScreenHeader
          title={t('createAnnouncement.title', 'Dodaj ogłoszenie')}
          onBackPress={() => {}}
        />

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ 
            paddingBottom: Platform.OS === 'web' && !isMobileLayout 
              ? insets.bottom + 40 
              : insets.bottom + 100 
          }}
          showsVerticalScrollIndicator={false}
        >
          {Platform.OS === 'web' && !isMobileLayout ? (
            // Desktop/Tablet: Two-container layout
            <YStack padding="$4" gap="$4" maxWidth={1200} alignSelf="center" width="100%">
              {/* Top Container: Photo + Meeting Type, Title, Description */}
              <YStack
                backgroundColor={cardBg}
                borderRadius="$4"
                padding="$4"
                borderWidth={1}
                borderColor={borderColor}
                gap="$4"
              >
                <XStack gap="$4" width="100%" justifyContent="space-between">
                  {/* Left Column - Photos */}
                  <YStack flex={1} gap="$4" maxWidth="48%">
                    {/* Photo Upload Section */}
                    <YStack gap="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.addPhoto', 'Dodaj zdjęcie klimatu lub miejsca')}
                      </Text>
                      <YStack gap="$2">
                        {[0, 1].map((index) => (
                          <YStack key={index} position="relative">
                            {selectedPhotos[index] ? (
                              <YStack position="relative">
                                <Image
                                  source={{
                                    uri: Platform.OS === 'web'
                                      ? URL.createObjectURL(selectedPhotos[index] as File)
                                      : (selectedPhotos[index] as { uri: string }).uri,
                                  }}
                                  style={{
                                    width: '100%',
                                    height: 120,
                                    borderRadius: 12,
                                    backgroundColor: chipBg,
                                  }}
                                  resizeMode="cover"
                                />
                                <TouchableOpacity
                                  onPress={() => handlePhotoRemove(index)}
                                  style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    width: 28,
                                    height: 28,
                                    borderRadius: 14,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <X size={16} color={COLORS.WHITE} />
                                </TouchableOpacity>
                              </YStack>
                            ) : (
                              <>
                                {Platform.OS === 'web' ? (
                                  <>
                                    <input
                                      ref={(el) => {
                                        fileInputRefs.current[index] = el;
                                      }}
                                      type="file"
                                      accept="image/jpeg,image/png,image/gif,image/webp"
                                      style={{ display: 'none' }}
                                      onChange={(e) => handleFileInputChange(index, e)}
                                    />
                                    <TouchableOpacity
                                      style={getPhotoUploadStyle(isDark, borderColor)}
                                      onPress={() => fileInputRefs.current[index]?.click()}
                                    >
                                      <Upload size={24} color={subtextColor} />
                                      <Text fontSize="$3" color={subtextColor} marginTop="$2">
                                        {t('createAnnouncement.addPhotoButton', 'Dodaj zdjęcie')}
                                      </Text>
                                    </TouchableOpacity>
                                  </>
                                ) : (
                                  <TouchableOpacity
                                    style={getPhotoUploadStyle(isDark, borderColor)}
                                    onPress={() => {
                                      Alert.alert(
                                        t('createAnnouncement.addPhoto', 'Dodaj zdjęcie'),
                                        t('createAnnouncement.nativePhotoPicker', 'Funkcja wyboru zdjęć będzie dostępna wkrótce')
                                      );
                                    }}
                                  >
                                    <Upload size={24} color={subtextColor} />
                                    <Text fontSize="$3" color={subtextColor} marginTop="$2">
                                      {t('createAnnouncement.addPhotoButton', 'Dodaj zdjęcie')}
                                    </Text>
                                  </TouchableOpacity>
                                )}
                              </>
                            )}
                          </YStack>
                        ))}
                      </YStack>
                    </YStack>
                  </YStack>

                  {/* Right Column - Meeting Type, Title, Description */}
                  <YStack flex={1} gap="$4" maxWidth="48%">
                    {/* Meeting Type Selection */}
                    <YStack gap="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.selectMeetingType', 'Wybierz typ spotkania:')}
                      </Text>
                      {isLoadingTypes ? (
                        <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                      ) : (
                        <XStack flexWrap="wrap" gap="$2">
                          {visibleTypes.map((type) => (
                            <TouchableOpacity
                              key={type.id}
                              onPress={() => toggleMeetingType(type.id)}
                              style={getChipStyle(
                                selectedMeetingTypeIds.includes(type.id),
                                chipBg,
                                chipSelectedBg,
                                borderColor
                              )}
                            >
                              <Text
                                fontSize="$3"
                                color={selectedMeetingTypeIds.includes(type.id) ? COLORS.WHITE : textColor}
                              >
                                {type.name}
                              </Text>
                            </TouchableOpacity>
                          ))}
                          {moreTypes.length > 0 && (
                            <TouchableOpacity
                              onPress={() => setShowMoreTypes(true)}
                              style={getChipStyle(false, chipBg, chipSelectedBg, borderColor)}
                            >
                              <Text fontSize="$3" color={textColor}>
                                {t('createAnnouncement.more', 'Więcej')}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </XStack>
                      )}
                    </YStack>

                    {/* Title Section */}
                    <YStack gap="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.announcementTitle', 'Tytuł ogłoszenia')}
                      </Text>
                      <TextInput
                        style={getSingleLineInputStyle(inputBg, borderColor, textColor)}
                        placeholder={t('createAnnouncement.titlePlaceholder', 'Np. Wieczór filmowy - kto się wybiera?')}
                        placeholderTextColor={subtextColor}
                        value={announcementTitle}
                        onChangeText={setAnnouncementTitle}
                      />
                    </YStack>

                    {/* Additional Info */}
                    <YStack gap="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.description', 'Opis')}
                      </Text>
                      <TextInput
                        style={getMultilineInputStyle(inputBg, borderColor, textColor)}
                        placeholder={t('createAnnouncement.descriptionPlaceholder', 'Opisz szczegółowo czego szukasz, co planujesz...')}
                        placeholderTextColor={subtextColor}
                        value={additionalInfo}
                        onChangeText={setAdditionalInfo}
                        multiline
                        numberOfLines={4}
                      />
                    </YStack>
                  </YStack>
                </XStack>
              </YStack>

              {/* Bottom Containers: Calendar + Time | Location, Age Range, Payment, Participants */}
              <XStack gap="$4" width="100%" justifyContent="space-between">
                {/* Left Container - Calendar + Time */}
                <YStack
                  flex={1}
                  maxWidth="48%"
                  gap="$4"
                  backgroundColor={cardBg}
                  borderRadius="$4"
                  padding="$4"
                  borderWidth={1}
                  borderColor={borderColor}
                >
                  {/* Calendar Section */}
                  <YStack gap="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('createAnnouncement.whenMeeting', 'Kiedy planujesz spotkanie?')}
                    </Text>
                    <YStack
                      backgroundColor={inputBg}
                      borderRadius="$4"
                      padding="$3"
                      borderWidth={1}
                      borderColor={borderColor}
                    >
                      {/* Month/Year Header */}
                      <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                        <TouchableOpacity onPress={handlePrevMonth}>
                          <ChevronLeft size={24} color={textColor} />
                        </TouchableOpacity>
                        <Text fontSize="$4" fontWeight="600" color={textColor}>
                          {MONTHS_PL[calendarMonth]} {calendarYear}
                        </Text>
                        <TouchableOpacity onPress={handleNextMonth}>
                          <ChevronRight size={24} color={textColor} />
                        </TouchableOpacity>
                      </XStack>

                      {/* Days of Week Header */}
                      <XStack justifyContent="space-around" marginBottom="$2">
                        {DAYS_OF_WEEK_PL.map((day) => (
                          <Text
                            key={day}
                            fontSize="$2"
                            color={subtextColor}
                            width={36}
                            textAlign="center"
                          >
                            {day}
                          </Text>
                        ))}
                      </XStack>

                      {/* Calendar Grid */}
                      <XStack flexWrap="wrap">
                        {calendarDays.map((day, index) => (
                          <TouchableOpacity
                            key={index}
                            style={getDayStyle(day, isDateSelected(day || 0), isToday(day || 0), isDark)}
                            onPress={() => day && handleSelectDate(day)}
                            disabled={!day}
                          >
                            {day && (
                              <Text
                                fontSize="$3"
                                color={
                                  isDateSelected(day)
                                    ? COLORS.WHITE
                                    : isToday(day)
                                    ? COLORS.PRIMARY
                                    : textColor
                                }
                                fontWeight={isToday(day) || isDateSelected(day) ? '700' : '400'}
                              >
                                {day}
                              </Text>
                            )}
                          </TouchableOpacity>
                        ))}
                      </XStack>
                    </YStack>
                  </YStack>

                  {/* Time Selection */}
                  <YStack gap="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('createAnnouncement.selectTime', 'Wybierz czas')}
                    </Text>
                    {Platform.OS === 'web' ? (
                      <TouchableOpacity
                        style={getInputContainerStyle(inputBg, borderColor)}
                        onPress={() => {
                          const input = document.getElementById('time-picker-desktop') as HTMLInputElement;
                          if (input) input.showPicker();
                        }}
                      >
                        <Text
                          fontSize="$3"
                          color={selectedTime ? textColor : subtextColor}
                        >
                          {selectedTime || 'gg:mm'}
                        </Text>
                        <ChevronDown size={20} color={subtextColor} />
                        <input
                          id="time-picker-desktop"
                          type="time"
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: 0,
                            height: 0,
                            pointerEvents: 'none',
                          }}
                        />
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={getInputContainerStyle(inputBg, borderColor)}
                        onPress={() => setShowTimePicker(true)}
                      >
                        <Text
                          fontSize="$3"
                          color={selectedTime ? textColor : subtextColor}
                        >
                          {selectedTime || 'gg:mm'}
                        </Text>
                        <ChevronDown size={20} color={subtextColor} />
                      </TouchableOpacity>
                    )}
                  </YStack>
                </YStack>

                {/* Right Container - Location, Age Range, Payment, Participants */}
                <YStack
                  flex={1}
                  maxWidth="48%"
                  gap="$4"
                  backgroundColor={cardBg}
                  borderRadius="$4"
                  padding="$4"
                  borderWidth={1}
                  borderColor={borderColor}
                >
                  {/* Location Section */}
                  <YStack gap="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('createAnnouncement.whereToMeet', 'Gdzie ma się odbyć?')}
                    </Text>
                    <YStack
                      backgroundColor={inputBg}
                      borderRadius="$3"
                      borderWidth={1}
                      borderColor={borderColor}
                      padding="$3"
                      gap="$3"
                    >
                      <YStack position="relative">
                        <XStack alignItems="center" gap="$2">
                          <MapPin size={20} color={subtextColor} />
                          <TextInput
                            style={getTextInputStyle(textColor)}
                            placeholder={t('createAnnouncement.addressPlaceholder', 'Wyszukaj obszar, nazwę ulicy, lokalizację...')}
                            placeholderTextColor={subtextColor}
                            value={addressSearchQuery || meetingAddress}
                            onChangeText={(text) => {
                              setAddressSearchQuery(text);
                              setShowLocationDropdown(true);
                              if (!text) {
                                setMeetingAddress('');
                                setSelectedLocation(null);
                              }
                            }}
                            onFocus={() => setShowLocationDropdown(true)}
                          />
                          {(isSearchingLocations || isGettingLocation) && (
                            <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                          )}
                        </XStack>
                        
                        {/* Location search results dropdown */}
                        {showLocationDropdown && locationResults.length > 0 && addressSearchQuery.length >= 2 && (
                          <YStack
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            backgroundColor={cardBg}
                            borderRadius="$2"
                            borderWidth={1}
                            borderColor={borderColor}
                            marginTop="$1"
                            zIndex={1000}
                            maxHeight={200}
                            overflow="hidden"
                          >
                            <ScrollView nestedScrollEnabled>
                              {locationResults.map((location, index) => (
                                <TouchableOpacity
                                  key={`${location.lat}-${location.lon}-${index}`}
                                  onPress={() => handleLocationSelect(location)}
                                  style={{
                                    paddingHorizontal: 12,
                                    paddingVertical: 10,
                                    borderBottomWidth: index < locationResults.length - 1 ? 1 : 0,
                                    borderBottomColor: borderColor,
                                  }}
                                >
                                  <Text fontSize="$3" color={textColor} numberOfLines={2}>
                                    {location.displayName}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          </YStack>
                        )}
                      </YStack>
                      
                      <TouchableOpacity
                        onPress={handleGetCurrentLocation}
                        disabled={isGettingLocation}
                        style={{
                          paddingVertical: 8,
                          opacity: isGettingLocation ? 0.6 : 1,
                        }}
                      >
                        <XStack alignItems="center" gap="$2">
                          <Navigation size={18} color={COLORS.PRIMARY} />
                          <Text fontSize="$3" color={COLORS.PRIMARY} fontWeight="500">
                            {isGettingLocation 
                              ? t('createAnnouncement.gettingLocation', 'Pobieranie lokalizacji...')
                              : t('createAnnouncement.useCurrentLocation', 'Użyj bieżącej lokalizacji')
                            }
                          </Text>
                        </XStack>
                      </TouchableOpacity>
                    </YStack>
                  </YStack>

                  {/* Age Range Slider */}
                  <YStack gap="$2">
                    <XStack justifyContent="space-between" alignItems="center">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.ageRange', 'Przedział wiekowy')}
                      </Text>
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {ageFrom}–{ageTo}+
                      </Text>
                    </XStack>
                    <RangeSlider
                      min={18}
                      max={100}
                      valueFrom={ageFrom}
                      valueTo={ageTo}
                      onValueChange={(from, to) => {
                        setAgeFrom(from);
                        setAgeTo(to);
                      }}
                      trackColor={chipBg}
                      activeTrackColor={COLORS.PRIMARY}
                      thumbColor={COLORS.PRIMARY}
                      isDark={isDark}
                    />
                  </YStack>

                  {/* Payment Amount */}
                  <YStack gap="$2">
                    <XStack justifyContent="space-between" alignItems="center">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.paymentAmount', 'Ile płacisz za spotkanie?')}
                      </Text>
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {paymentAmount}zł
                      </Text>
                    </XStack>
                    <Text fontSize="$2" color={subtextColor}>
                      {t('createAnnouncement.paymentHint', 'Stan konta: 225,00 zł')}
                    </Text>
                    <SingleSlider
                      min={0}
                      max={500}
                      value={paymentAmount}
                      onValueChange={setPaymentAmount}
                      trackColor={chipBg}
                      activeTrackColor={COLORS.PRIMARY}
                      thumbColor={COLORS.PRIMARY}
                      isDark={isDark}
                    />
                  </YStack>

                  {/* Participant Count */}
                  <YStack gap="$2">
                    <XStack justifyContent="space-between" alignItems="center">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('createAnnouncement.participantCount', 'Ile osób szukasz?')}
                      </Text>
                      <Text fontSize="$3" color={subtextColor}>
                        {t('createAnnouncement.participantRange', 'liczba')}: {participantCountFrom === participantCountTo ? `${participantCountFrom} ${participantCountFrom === 1 ? 'osoba' : participantCountFrom < 5 ? 'osoby' : 'osób'}` : `${participantCountFrom}–${participantCountTo}`}
                      </Text>
                    </XStack>
                    <RangeSlider
                      min={1}
                      max={10}
                      valueFrom={participantCountFrom}
                      valueTo={participantCountTo}
                      onValueChange={(from, to) => {
                        setParticipantCountFrom(from);
                        setParticipantCountTo(to);
                      }}
                      trackColor={chipBg}
                      activeTrackColor={COLORS.PRIMARY}
                      thumbColor={COLORS.PRIMARY}
                      isDark={isDark}
                    />
                  </YStack>
                </YStack>
              </XStack>

              {/* Publish Button - Desktop/Tablet */}
              <YStack paddingTop="$4" width="100%">
                <TouchableOpacity
                  style={getPublishButtonStyle(isCreating)}
                  onPress={handlePublish}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <ActivityIndicator color={COLORS.WHITE} />
                  ) : (
                    <Text fontSize="$4" fontWeight="600" color={COLORS.WHITE}>
                      {t('createAnnouncement.publish', 'Opublikuj ogłoszenie')}
                    </Text>
                  )}
                </TouchableOpacity>
              </YStack>
            </YStack>
          ) : (
            // Mobile: Single column layout
            <YStack padding="$4" gap="$4">
              {/* Photo Upload Section */}
              <YStack gap="$2">
                <Text fontSize="$4" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.addPhoto', 'Dodaj zdjęcie klimatu lub miejsca')}
                </Text>
                <YStack gap="$2">
                  {[0, 1].map((index) => (
                    <YStack key={index} position="relative">
                      {selectedPhotos[index] ? (
                        <YStack position="relative">
                          <Image
                            source={{
                              uri: Platform.OS === 'web'
                                ? URL.createObjectURL(selectedPhotos[index] as File)
                                : (selectedPhotos[index] as { uri: string }).uri,
                            }}
                            style={{
                              width: '100%',
                              height: 120,
                              borderRadius: 12,
                              backgroundColor: chipBg,
                            }}
                            resizeMode="cover"
                          />
                          <TouchableOpacity
                            onPress={() => handlePhotoRemove(index)}
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              borderRadius: 14,
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <X size={16} color={COLORS.WHITE} />
                          </TouchableOpacity>
                        </YStack>
                      ) : (
                        <>
                          {Platform.OS === 'web' ? (
                            <>
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[index] = el;
                                }}
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileInputChange(index, e)}
                              />
                              <TouchableOpacity
                                style={getPhotoUploadStyle(isDark, borderColor)}
                                onPress={() => fileInputRefs.current[index]?.click()}
                              >
                                <Upload size={24} color={subtextColor} />
                                <Text fontSize="$3" color={subtextColor} marginTop="$2">
                                  {t('createAnnouncement.addPhotoButton', 'Dodaj zdjęcie')}
                                </Text>
                              </TouchableOpacity>
                            </>
                          ) : (
                            <TouchableOpacity
                              style={getPhotoUploadStyle(isDark, borderColor)}
                              onPress={() => {
                                // For native, you would use ImagePicker here
                                // For now, show an alert
                                Alert.alert(
                                  t('createAnnouncement.addPhoto', 'Dodaj zdjęcie'),
                                  t('createAnnouncement.nativePhotoPicker', 'Funkcja wyboru zdjęć będzie dostępna wkrótce')
                                );
                              }}
                            >
                              <Upload size={24} color={subtextColor} />
                              <Text fontSize="$3" color={subtextColor} marginTop="$2">
                                {t('createAnnouncement.addPhotoButton', 'Dodaj zdjęcie')}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </>
                      )}
                    </YStack>
                  ))}
                </YStack>
              </YStack>

              {/* Meeting Type Selection */}
              <YStack gap="$2">
                <Text fontSize="$4" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.selectMeetingType', 'Wybierz typ spotkania:')}
                </Text>
                {isLoadingTypes ? (
                  <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                ) : (
                  <XStack flexWrap="wrap" gap="$2">
                    {visibleTypes.map((type) => (
                      <TouchableOpacity
                        key={type.id}
                        onPress={() => toggleMeetingType(type.id)}
                        style={getChipStyle(
                          selectedMeetingTypeIds.includes(type.id),
                          chipBg,
                          chipSelectedBg,
                          borderColor
                        )}
                      >
                        <Text
                          fontSize="$3"
                          color={selectedMeetingTypeIds.includes(type.id) ? COLORS.WHITE : textColor}
                        >
                          {type.name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                    {moreTypes.length > 0 && (
                      <TouchableOpacity
                        onPress={() => setShowMoreTypes(true)}
                        style={getChipStyle(false, chipBg, chipSelectedBg, borderColor)}
                      >
                        <Text fontSize="$3" color={textColor}>
                          {t('createAnnouncement.more', 'Więcej')}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </XStack>
                )}
              </YStack>

              {/* Calendar Section */}
              <YStack gap="$2">
                <Text fontSize="$4" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.whenMeeting', 'Kiedy planujesz spotkanie?')}
                </Text>
                <YStack
                  backgroundColor={cardBg}
                  borderRadius="$4"
                  padding="$3"
                  borderWidth={1}
                  borderColor={borderColor}
                >
                  {/* Month/Year Header */}
                  <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                    <TouchableOpacity onPress={handlePrevMonth}>
                      <ChevronLeft size={24} color={textColor} />
                    </TouchableOpacity>
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {MONTHS_PL[calendarMonth]} {calendarYear}
                    </Text>
                    <TouchableOpacity onPress={handleNextMonth}>
                      <ChevronRight size={24} color={textColor} />
                    </TouchableOpacity>
                  </XStack>

                  {/* Days of Week Header */}
                  <XStack justifyContent="space-around" marginBottom="$2">
                    {DAYS_OF_WEEK_PL.map((day) => (
                      <Text
                        key={day}
                        fontSize="$2"
                        color={subtextColor}
                        width={36}
                        textAlign="center"
                      >
                        {day}
                      </Text>
                    ))}
                  </XStack>

                  {/* Calendar Grid */}
                  <XStack flexWrap="wrap">
                    {calendarDays.map((day, index) => (
                      <TouchableOpacity
                        key={index}
                        style={getDayStyle(day, isDateSelected(day || 0), isToday(day || 0), isDark)}
                        onPress={() => day && handleSelectDate(day)}
                        disabled={!day}
                      >
                        {day && (
                          <Text
                            fontSize="$3"
                            color={
                              isDateSelected(day)
                                ? COLORS.WHITE
                                : isToday(day)
                                ? COLORS.PRIMARY
                                : textColor
                            }
                            fontWeight={isToday(day) || isDateSelected(day) ? '700' : '400'}
                          >
                            {day}
                          </Text>
                        )}
                      </TouchableOpacity>
                    ))}
                  </XStack>
                </YStack>
              </YStack>

              {/* Time Selection */}
              <YStack gap="$2">
                <Text fontSize="$4" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.selectTime', 'Wybierz czas')}
                </Text>
                {Platform.OS === 'web' ? (
                  <TouchableOpacity
                    style={getInputContainerStyle(inputBg, borderColor)}
                    onPress={() => {
                      const input = document.getElementById('time-picker-mobile') as HTMLInputElement;
                      if (input) input.showPicker();
                    }}
                  >
                    <Text
                      fontSize="$3"
                      color={selectedTime ? textColor : subtextColor}
                    >
                      {selectedTime || 'gg:mm'}
                    </Text>
                    <ChevronDown size={20} color={subtextColor} />
                    <input
                      id="time-picker-mobile"
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: 0,
                        height: 0,
                        pointerEvents: 'none',
                      }}
                    />
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={getInputContainerStyle(inputBg, borderColor)}
                    onPress={() => setShowTimePicker(true)}
                  >
                    <Text
                      fontSize="$3"
                      color={selectedTime ? textColor : subtextColor}
                    >
                      {selectedTime || 'gg:mm'}
                    </Text>
                    <ChevronDown size={20} color={subtextColor} />
                  </TouchableOpacity>
                )}
              </YStack>

              {/* Location Section */}
              <YStack gap="$2">
                <Text fontSize="$4" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.whereToMeet', 'Gdzie ma się odbyć?')}
                </Text>
                <YStack
                  backgroundColor={inputBg}
                  borderRadius="$3"
                  borderWidth={1}
                  borderColor={borderColor}
                  padding="$3"
                  gap="$3"
                >
                  <YStack position="relative" zIndex={100}>
                    <XStack alignItems="center" gap="$2">
                      <MapPin size={20} color={subtextColor} />
                      <TextInput
                        style={getTextInputStyle(textColor)}
                        placeholder={t('createAnnouncement.addressPlaceholder', 'Wyszukaj miasto, nazwę ulicy, lokalizację...')}
                        placeholderTextColor={subtextColor}
                        value={addressSearchQuery || meetingAddress}
                        onChangeText={(text) => {
                          setAddressSearchQuery(text);
                          setShowLocationDropdown(true);
                          if (!text) {
                            setMeetingAddress('');
                            setSelectedLocation(null);
                          }
                        }}
                        onFocus={() => setShowLocationDropdown(true)}
                      />
                      {(isSearchingLocations || isGettingLocation) && (
                        <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                      )}
                    </XStack>
                    
                    {/* Location search results dropdown */}
                    {showLocationDropdown && locationResults.length > 0 && addressSearchQuery.length >= 2 && (
                      <YStack
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        backgroundColor={cardBg}
                        borderRadius="$2"
                        borderWidth={1}
                        borderColor={borderColor}
                        marginTop="$1"
                        zIndex={1000}
                        maxHeight={200}
                        overflow="hidden"
                      >
                        <ScrollView nestedScrollEnabled>
                          {locationResults.map((location, index) => (
                            <TouchableOpacity
                              key={`${location.lat}-${location.lon}-${index}`}
                              onPress={() => handleLocationSelect(location)}
                              style={{
                                paddingHorizontal: 12,
                                paddingVertical: 10,
                                borderBottomWidth: index < locationResults.length - 1 ? 1 : 0,
                                borderBottomColor: borderColor,
                              }}
                            >
                              <Text fontSize="$3" color={textColor} numberOfLines={2}>
                                {location.displayName}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </YStack>
                    )}
                  </YStack>
                  
                  <TouchableOpacity
                    onPress={handleGetCurrentLocation}
                    disabled={isGettingLocation}
                    style={{
                      paddingVertical: 8,
                      opacity: isGettingLocation ? 0.6 : 1,
                    }}
                  >
                    <XStack alignItems="center" gap="$2">
                      <Navigation size={18} color={COLORS.PRIMARY} />
                      <Text fontSize="$3" color={COLORS.PRIMARY} fontWeight="500">
                        {isGettingLocation 
                          ? t('createAnnouncement.gettingLocation', 'Pobieranie lokalizacji...')
                          : t('createAnnouncement.useCurrentLocation', 'Użyj bieżącej lokalizacji')
                        }
                      </Text>
                    </XStack>
                  </TouchableOpacity>
                </YStack>
              </YStack>

              {/* Additional Info */}
              <YStack gap="$2">
                <Text fontSize="$4" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.additionalInfo', 'Dodatkowe informacje (opcjonalnie)')}
                </Text>
                <TextInput
                  style={getMultilineInputStyle(inputBg, borderColor, textColor)}
                  placeholder={t('createAnnouncement.additionalInfoPlaceholder', 'Czego oczekujesz? Jak się ubrać?')}
                  placeholderTextColor={subtextColor}
                  value={additionalInfo}
                  onChangeText={setAdditionalInfo}
                  multiline
                  numberOfLines={3}
                />
              </YStack>

              {/* Age Range Slider */}
              <YStack gap="$2">
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="$4" fontWeight="600" color={textColor}>
                    {t('createAnnouncement.ageRange', 'Przedział wiekowy')}
                  </Text>
                  <Text fontSize="$4" fontWeight="600" color={textColor}>
                    {ageFrom}–{ageTo}+
                  </Text>
                </XStack>
                <RangeSlider
                  min={18}
                  max={100}
                  valueFrom={ageFrom}
                  valueTo={ageTo}
                  onValueChange={(from, to) => {
                    setAgeFrom(from);
                    setAgeTo(to);
                  }}
                  trackColor={chipBg}
                  activeTrackColor={COLORS.PRIMARY}
                  thumbColor={COLORS.PRIMARY}
                  isDark={isDark}
                />
              </YStack>

              {/* Participant Count */}
              <YStack gap="$2">
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="$4" fontWeight="600" color={textColor}>
                    {t('createAnnouncement.participantCount', 'Ile osób szukasz?')}
                  </Text>
                  <Text fontSize="$3" color={subtextColor}>
                    {t('createAnnouncement.participantRange', 'liczba')}: {participantCountFrom === participantCountTo ? `${participantCountFrom} ${participantCountFrom === 1 ? 'osoba' : participantCountFrom < 5 ? 'osoby' : 'osób'}` : `${participantCountFrom}–${participantCountTo}`}
                  </Text>
                </XStack>
                <RangeSlider
                  min={1}
                  max={10}
                  valueFrom={participantCountFrom}
                  valueTo={participantCountTo}
                  onValueChange={(from, to) => {
                    setParticipantCountFrom(from);
                    setParticipantCountTo(to);
                  }}
                  trackColor={chipBg}
                  activeTrackColor={COLORS.PRIMARY}
                  thumbColor={COLORS.PRIMARY}
                  isDark={isDark}
                />
              </YStack>

              {/* Payment Amount */}
              <YStack gap="$2">
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="$4" fontWeight="600" color={textColor}>
                    {t('createAnnouncement.paymentAmount', 'Ile płacisz za spotkanie?')}
                  </Text>
                  <Text fontSize="$4" fontWeight="600" color={textColor}>
                    {paymentAmount}zł
                  </Text>
                </XStack>
                <Text fontSize="$2" color={subtextColor}>
                  {t('createAnnouncement.paymentHint', 'Stan konta: 215,00 zł')}
                </Text>
                <SingleSlider
                  min={0}
                  max={500}
                  value={paymentAmount}
                  onValueChange={setPaymentAmount}
                  trackColor={chipBg}
                  activeTrackColor={COLORS.PRIMARY}
                  thumbColor={COLORS.PRIMARY}
                  isDark={isDark}
                />
              </YStack>

              {/* Publish Button - Mobile */}
              <YStack paddingTop="$4" paddingBottom="$4">
                <TouchableOpacity
                  style={getPublishButtonStyle(isCreating)}
                  onPress={handlePublish}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <ActivityIndicator color={COLORS.WHITE} />
                  ) : (
                    <Text fontSize="$4" fontWeight="600" color={COLORS.WHITE}>
                      {t('createAnnouncement.publish', 'Opublikuj ogłoszenie')}
                    </Text>
                  )}
                </TouchableOpacity>
              </YStack>
            </YStack>
          )}
        </ScrollView>

        {/* More Types Modal */}
        <Modal
          visible={showMoreTypes}
          transparent
          animationType="slide"
          onRequestClose={() => setShowMoreTypes(false)}
        >
          <YStack flex={1} backgroundColor="rgba(0,0,0,0.5)" justifyContent="flex-end">
            <YStack
              backgroundColor={cardBg}
              borderTopLeftRadius={20}
              borderTopRightRadius={20}
              padding="$4"
              maxHeight="60%"
            >
              <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
                <Text fontSize="$5" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.moreTypes', 'Więcej typów spotkań')}
                </Text>
                <TouchableOpacity onPress={() => setShowMoreTypes(false)}>
                  <X size={24} color={textColor} />
                </TouchableOpacity>
              </XStack>
              <FlatList
                data={moreTypes}
                keyExtractor={(item) => `type-${item.id}`}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={getMoreTypeItemStyle(
                      selectedMeetingTypeIds.includes(item.id),
                      chipBg,
                      chipSelectedBg,
                      borderColor
                    )}
                    onPress={() => toggleMeetingType(item.id)}
                  >
                    <Text
                      fontSize="$4"
                      color={selectedMeetingTypeIds.includes(item.id) ? COLORS.WHITE : textColor}
                    >
                      {item.name}
                    </Text>
                  </TouchableOpacity>
                )}
                showsVerticalScrollIndicator={false}
              />
            </YStack>
          </YStack>
        </Modal>

        {/* Time Picker Modal */}
        <Modal
          visible={showTimePicker}
          transparent
          animationType="slide"
          onRequestClose={() => setShowTimePicker(false)}
        >
          <YStack flex={1} backgroundColor="rgba(0,0,0,0.5)" justifyContent="flex-end">
            <YStack
              backgroundColor={cardBg}
              borderTopLeftRadius={20}
              borderTopRightRadius={20}
              padding="$4"
            >
              <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
                <Text fontSize="$5" fontWeight="600" color={textColor}>
                  {t('createAnnouncement.selectTimeTitle', 'Wybierz godzinę')}
                </Text>
                <TouchableOpacity onPress={() => setShowTimePicker(false)}>
                  <X size={24} color={textColor} />
                </TouchableOpacity>
              </XStack>
              <XStack height={200} justifyContent="center" gap="$4">
                {/* Hours */}
                <YStack flex={1}>
                  <Text fontSize="$3" color={subtextColor} textAlign="center" marginBottom="$2">
                    {t('createAnnouncement.hour', 'Godzina')}
                  </Text>
                  <FlatList
                    data={hours}
                    keyExtractor={(item) => `hour-${item}`}
                    showsVerticalScrollIndicator={false}
                    style={getPickerListStyle(isDark)}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => {
                          const currentMinute = selectedTime.split(':')[1] || '00';
                          setSelectedTime(`${item}:${currentMinute}`);
                        }}
                        style={getPickerItemStyle(
                          selectedTime.startsWith(`${item}:`),
                          isDark
                        )}
                      >
                        <Text
                          color={selectedTime.startsWith(`${item}:`) ? COLORS.WHITE : textColor}
                          textAlign="center"
                          fontWeight={selectedTime.startsWith(`${item}:`) ? '600' : '400'}
                        >
                          {item}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                </YStack>

                {/* Minutes */}
                <YStack flex={1}>
                  <Text fontSize="$3" color={subtextColor} textAlign="center" marginBottom="$2">
                    {t('createAnnouncement.minute', 'Minuta')}
                  </Text>
                  <FlatList
                    data={minutes}
                    keyExtractor={(item) => `minute-${item}`}
                    showsVerticalScrollIndicator={false}
                    style={getPickerListStyle(isDark)}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => {
                          const currentHour = selectedTime.split(':')[0] || '12';
                          setSelectedTime(`${currentHour}:${item}`);
                        }}
                        style={getPickerItemStyle(
                          selectedTime.endsWith(`:${item}`),
                          isDark
                        )}
                      >
                        <Text
                          color={selectedTime.endsWith(`:${item}`) ? COLORS.WHITE : textColor}
                          textAlign="center"
                          fontWeight={selectedTime.endsWith(`:${item}`) ? '600' : '400'}
                        >
                          {item}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                </YStack>
              </XStack>
              <TouchableOpacity
                style={getConfirmButtonStyle()}
                onPress={() => setShowTimePicker(false)}
              >
                <Text color={COLORS.WHITE} fontSize="$4" fontWeight="600">
                  {t('common.confirm', 'Potwierdź')}
                </Text>
              </TouchableOpacity>
            </YStack>
          </YStack>
        </Modal>
      </YStack>
    </Screen>
  );
};

// Style functions
function getPhotoUploadStyle(isDark: boolean, borderColor: string): ViewStyle {
  return {
    height: 120,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: borderColor,
    backgroundColor: isDark ? '#1a1a1a' : '#fafafa',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function getChipStyle(
  isSelected: boolean,
  chipBg: string,
  chipSelectedBg: string,
  borderColor: string
): ViewStyle {
  return {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: isSelected ? chipSelectedBg : chipBg,
    borderWidth: 1,
    borderColor: isSelected ? chipSelectedBg : borderColor,
  };
}

function getDayStyle(
  day: number | null,
  isSelected: boolean,
  _isToday: boolean,
  _isDark: boolean
): ViewStyle {
  return {
    width: '14.28%',
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    backgroundColor: isSelected ? COLORS.PRIMARY : 'transparent',
  };
}

function getInputContainerStyle(inputBg: string, borderColor: string): ViewStyle {
  return {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: inputBg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: borderColor,
    paddingHorizontal: 16,
    paddingVertical: 14,
  };
}

function getTextInputStyle(textColor: string): TextStyle {
  return {
    flex: 1,
    fontSize: 14,
    color: textColor,
  };
}

function getSingleLineInputStyle(
  inputBg: string,
  borderColor: string,
  textColor: string
): TextStyle {
  return {
    backgroundColor: inputBg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: borderColor,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 14,
    color: textColor,
  };
}

function getMultilineInputStyle(
  inputBg: string,
  borderColor: string,
  textColor: string
): TextStyle {
  return {
    backgroundColor: inputBg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: borderColor,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    color: textColor,
    minHeight: 80,
    textAlignVertical: 'top',
  };
}

function getPublishButtonStyle(isLoading: boolean): ViewStyle {
  return {
    backgroundColor: isLoading ? COLORS.GRAY_400 : COLORS.PRIMARY,
    borderRadius: 25,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function getMoreTypeItemStyle(
  isSelected: boolean,
  chipBg: string,
  chipSelectedBg: string,
  borderColor: string
): ViewStyle {
  return {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: isSelected ? chipSelectedBg : chipBg,
    borderWidth: 1,
    borderColor: isSelected ? chipSelectedBg : borderColor,
    marginBottom: 8,
  };
}

function getPickerListStyle(isDark: boolean): ViewStyle {
  return {
    backgroundColor: isDark ? '#2a2a2a' : '#f3f4f6',
    borderRadius: 8,
    flex: 1,
  };
}

function getPickerItemStyle(isSelected: boolean, _isDark: boolean): ViewStyle {
  return {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: isSelected ? COLORS.PRIMARY : 'transparent',
    borderRadius: 8,
    marginVertical: 2,
    marginHorizontal: 4,
  };
}

function getConfirmButtonStyle(): ViewStyle {
  return {
    backgroundColor: COLORS.PRIMARY,
    borderRadius: 25,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 16,
  };
}

export default CreateAnnouncementScreen;

